import React, { useEffect, useMemo } from 'react';
import {
    Block,
    Heading3,
    Heading4,
    FlexDiv,
    TextDefault,
    TextTiny,
    palette,
} from '@awarego-ui/components';
import { observer } from 'mobx-react';
import { useStores } from '../../../../hooks/use-stores';
import ThemedSpinner from '../../../../components/themed/ThemedSpinner';
import {
    calculateColor,
    replaceSpaceWithUnderscore,
} from '../../../../utils/helpers';
import { rgba } from 'polished';
import styled from 'styled-components';
import Tooltip from '@mui/material/Tooltip';
import { Link } from 'react-router-dom';
import { useAssessmentType } from '../../useAssessmentType';

const TiltedTextTiny = styled(TextTiny)`
    transform: rotate(-40deg);
    transform-origin: bottom left;
    position: relative;
    left: 24px;
    white-space: nowrap;
`;

function EmployeeListScoresPerThreat({ companyId, assessmentId, hasStarted }) {
    const { assessmentStore, assessmentUsersStoresFactory } = useAssessmentType();

    const { sortedListScoresPerThreatArea, loadingListScoresPerThreatArea } =
        assessmentStore;

    useEffect(() => {
        assessmentStore.loadListScoresPerThreatArea(companyId, assessmentId);
    }, [assessmentId, assessmentStore, companyId]);

    const visibleLists = useMemo(
        () =>
            (sortedListScoresPerThreatArea || []).filter(
                (l) => l.started && l.areas && l.areas.length
            ),
        [sortedListScoresPerThreatArea]
    );

    const allAreasOrder = useMemo(() => {
        const set = new Set();
        visibleLists.forEach((l) => l.areas.forEach((a) => set.add(a.area)));
        return Array.from(set);
    }, [visibleLists]);

    const maxListNameLength = 16;
    const shorten = (s) =>
        s.length > maxListNameLength ? s.slice(0, maxListNameLength) + '…' : s;

    const content = useMemo(() => {
        if (!hasStarted)
            return (
                <Heading4 center lighter my={24}>
                    No employee list scores yet
                </Heading4>
            );

        if (loadingListScoresPerThreatArea || !visibleLists.length)
            return <ThemedSpinner />;

        const topPad = visibleLists.length < 4 ? undefined : 100;

        return (
            <FlexDiv $noWrap margin={8}>
                <FlexDiv column $noWrap borderRight>
                    <FlexDiv pt={topPad} height={topPad ? 56 : 20} />
                    {allAreasOrder.map((area) => (
                        <FlexDiv
                            key={area}
                            alignCenter
                            justifyEnd
                            my={2}
                            mr={16}
                            positionSticky
                            height={56}
                        >
                            <TextDefault $noWrap bold link>
                                <Link to={replaceSpaceWithUnderscore(area)}>{area}</Link>
                            </TextDefault>
                        </FlexDiv>
                    ))}
                </FlexDiv>

                <FlexDiv $noWrap overflowXauto fullWidth>
                    {visibleLists.map((list) => {
                        const areaMap = Object.fromEntries(
                            list.areas.map((a) => [a.area, a.averageScore])
                        );

                        return (
                            <FlexDiv column key={list.listId} $noWrap flexGrow={1}>
                                <FlexDiv pt={topPad} column positionRelative minWidth={100}>
                                    <Tooltip
                                        title={list.listName}
                                        arrow
                                        placement="top"
                                        enterDelay={300}
                                    >
                                        <TiltedTextTiny
                                            color={rgba(palette.graphite.heavyMetal, 0.6)}
                                            bold
                                        >
                                            {`${shorten(list.listName)} (${list.usersCount})`}
                                        </TiltedTextTiny>
                                    </Tooltip>

                                    {allAreasOrder.map((area) => {
                                        const score = areaMap[area];
                                        const haveScore = score !== undefined;
                                        return (
                                            <FlexDiv
                                                key={area}
                                                alignCenter
                                                justifyCenter
                                                margin={2}
                                                height={56}
                                                minWidth={100}
                                                link={haveScore}
                                                backgroundColor={
                                                    haveScore
                                                        ? rgba(calculateColor(score), 0.1)
                                                        : 'transparent'
                                                }
                                                onClick={() => {
                                                    if (!haveScore) return;
                                                    assessmentUsersStoresFactory.modalEmployeesStore.clearSelectedListsInFilter();
                                                    assessmentUsersStoresFactory.modalEmployeesStore.toggleListSelected(
                                                        list.listId
                                                    );
                                                    assessmentUsersStoresFactory.showEmployeesModal(
                                                        'users',
                                                        {
                                                            threatArea: area,
                                                            scoreName: 'Threat area score',
                                                            subheading: `Threat area: ${area}`,
                                                            listId: [list.listId],
                                                        }
                                                    );
                                                }}
                                            >
                                                {haveScore && (
                                                    <Tooltip title="View employee scores">
                                                        <TextDefault bold color={calculateColor(score)}>
                                                            {score}
                                                        </TextDefault>
                                                    </Tooltip>
                                                )}
                                            </FlexDiv>
                                        );
                                    })}
                                </FlexDiv>
                            </FlexDiv>
                        );
                    })}
                </FlexDiv>
            </FlexDiv>
        );
    }, [
        allAreasOrder,
        assessmentUsersStoresFactory,
        hasStarted,
        loadingListScoresPerThreatArea,
        visibleLists,
    ]);

    return (
        <Block gap={24}>
            <Heading3>Employee list scores per threat area</Heading3>
            {content}
        </Block>
    );
}

export default observer(EmployeeListScoresPerThreat);
